import classNames from 'classnames';
import type {
  HTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  PointerEventHandler,
  TouchEventHandler,
} from 'react';
import React from 'react';
import * as styles from './Button.module.scss';
import LoadingIndicator from './LoadingIndicator';
import type { SVGIconSize } from './SVGIcon';
import SVGIcon from './SVGIcon';
import addTestId from '../utils/addTestId';
import Link from '../utils/router/Link';

type ButtonSize = 'large' | 'micro' | 'small' | 'regular';

export interface ButtonProps
  extends Pick<
    HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
    | 'aria-label'
    | 'aria-selected'
    | 'aria-expanded'
    | 'aria-disabled'
    | 'className'
    | 'children'
    | 'id'
    | 'role'
    | 'onContextMenu'
  > {
  active?: boolean;
  activeClassName?: string;
  block?: boolean;
  danger?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  onMouseDown?: MouseEventHandler;
  onClick?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  onBlur?: React.FocusEventHandler;
  onPointerDown?: PointerEventHandler;
  onKeyDown?: KeyboardEventHandler;
  onTouchStart?: TouchEventHandler;
  onlyActiveOnIndex?: boolean;
  size?: ButtonSize;
  target?: string;
  testId?: string;
  to?: React.ComponentProps<typeof Link>['to'];
  newTab?: React.ComponentProps<typeof Link>['newTab'];
  type?: 'button' | 'submit' | 'reset';
  variant?: 'link' | 'primary' | 'secondary' | 'muted';
  rel?: string;
  tabIndex?: number;
  'data-slab-slot'?: string; // TODO: not needed after https://github.com/slab/slab/issues/12726
}

const ButtonSizeToIconSize: Record<ButtonSize, SVGIconSize> = {
  large: 'large',
  micro: 'small',
  regular: 'regular',
  small: 'small',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      active = false,
      activeClassName,
      'aria-label': ariaLabel,
      'aria-selected': ariaSelected,
      'aria-expanded': ariaExpanded,
      'aria-disabled': ariaDisabled,
      block = false,
      children = null,
      className,
      danger = false,
      disabled = false,
      icon = null,
      id,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onBlur,
      onMouseDown,
      onPointerDown,
      onTouchStart,
      onKeyDown,
      onlyActiveOnIndex,
      size = 'regular',
      testId,
      to,
      type = 'button',
      variant,
      role,
      newTab,
      tabIndex,
      'data-slab-slot': dataSlabSlot,
      onContextMenu,
    }: ButtonProps,
    ref,
  ) => {
    const classes = classNames(styles.basic, className, {
      [styles.active]: active,
      [styles.block]: block,
      [styles.danger]: danger,
      [styles.disabled]: disabled,
      [styles.large]: size === 'large',
      [styles.link]: variant === 'link',
      [styles.micro]: size === 'micro',
      [styles.muted]: variant === 'muted',
      [styles.primary]: variant === 'primary',
      [styles.regularSize]: size === 'regular',
      [styles.secondary]: variant === 'secondary',
      [styles.small]: size === 'small',
    });

    const eventHandler = {
      onClick,
      onMouseEnter,
      onMouseLeave,
      onMouseDown,
      onPointerDown,
      onContextMenu,
      onBlur,
      onKeyDown,
      onTouchStart,
    };

    const iconSize = ButtonSizeToIconSize[size];

    const content = (
      <>
        {!!icon && (
          <div
            className={classNames(styles.iconContainer, {
              [styles.iconGap]: !!children,
            })}
            aria-hidden
          >
            {typeof icon === 'string' ? (
              <SVGIcon size={iconSize}>{icon}</SVGIcon>
            ) : (
              icon
            )}
          </div>
        )}
        {children}
      </>
    );

    if (to) {
      return (
        <Link
          className={classes}
          {...eventHandler}
          to={to}
          activeClassName={activeClassName}
          onlyActiveOnIndex={onlyActiveOnIndex}
          id={id}
          role={role}
          newTab={newTab}
          tabIndex={tabIndex}
          aria-selected={ariaSelected}
          aria-expanded={ariaExpanded}
          aria-disabled={ariaDisabled}
          {...addTestId(testId)}
          data-slab-slot={dataSlabSlot}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        className={classes}
        disabled={disabled}
        {...eventHandler}
        ref={ref}
        // eslint-disable-next-line react/button-has-type
        type={type}
        id={id}
        role={role}
        aria-label={ariaLabel}
        tabIndex={tabIndex}
        aria-selected={ariaSelected}
        aria-expanded={ariaExpanded}
        aria-disabled={ariaDisabled}
        {...addTestId(testId)}
        data-slab-slot={dataSlabSlot}
      >
        {content}
      </button>
    );
  },
);

export interface ButtonWithLoadingStateProps
  extends Omit<ButtonProps, 'variant'> {
  loading: boolean;
  variant?: Exclude<ButtonProps['variant'], 'link'>;
}

export const ButtonWithLoadingState = ({
  children,
  loading,
  disabled,
  ...props
}: ButtonWithLoadingStateProps) => {
  return (
    <Button disabled={disabled || loading} {...props}>
      <span
        className={classNames(styles.loadingStateContainer, {
          [styles.isLoading]: loading,
        })}
      >
        {loading && (
          <span className={styles.loadingIndicatorContainer}>
            <LoadingIndicator delay={0} adaptColor />
          </span>
        )}
        <span className={styles.inner}>{children}</span>
      </span>
    </Button>
  );
};

export default Button;
